import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ReviewList from './ReviewList';
import GuidedTour from './GuidedTour';
import ReviewImporter from './ReviewImporter';
import ComplianceFooter from './ComplianceFooter';
import { RefreshCw, Copy, ChevronLeft, LogOut, Sparkles, MessageSquare, RotateCcw, HelpCircle, Upload } from 'lucide-react';
import logo from '../assets/images/repugate_logo.png';
import { useKeyboardShortcuts, KeyboardShortcutsHelp } from '../utils/keyboardShortcuts';
import { sortReviewsByPriority } from '../utils/reviewPrioritization';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function Dashboard({ onLogout }) {
  const [page, setPage] = useState(null);
  const [pageAccessToken, setPageAccessToken] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [selectedReview, setSelectedReview] = useState(null);
  const [generatedReplies, setGeneratedReplies] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState('english');
  const [selectedTone, setSelectedTone] = useState('professional');
  const [selectedOption, setSelectedOption] = useState(null);
  const [showGuidedTour, setShowGuidedTour] = useState(false);
  const [showKeyboardHelp, setShowKeyboardHelp] = useState(false);
  const [showImporter, setShowImporter] = useState(false);
  const [firstCopyDone, setFirstCopyDone] = useState(false);
  const [stats, setStats] = useState({
    totalReviews: 0,
    repliesGenerated: 0,
    repliesSent: 0
  });

  // Tone presets
  const TONE_PRESETS = [
    { key: 'professional', label: '💼 Professional', description: 'Formal and business-appropriate' },
    { key: 'warm', label: '🤗 Warm', description: 'Friendly and personable' },
    { key: 'ApologyFirst', label: '😔 Apology-First', description: 'Empathetic and solution-focused' },
    { key: 'concise', label: '⚡ Concise', description: 'Brief and to the point' }
  ];

  // Restore session on mount
  useEffect(() => {
    const savedSession = localStorage.getItem('repugate_session') || sessionStorage.getItem('repugate_session');
    if (savedSession) {
      try {
        const session = JSON.parse(savedSession);
        
        // Validate session data
        if (!session.page || !session.page.id || !session.pageAccessToken) {
          throw new Error('Invalid session data');
        }
        
        setPage(session.page);
        setPageAccessToken(session.pageAccessToken);
        
        // Show guided tour on first visit
        const hasSeenTour = localStorage.getItem('repugate_tour_seen');
        if (!hasSeenTour) {
          setShowGuidedTour(true);
          localStorage.setItem('repugate_tour_seen', 'true');
        }
        
        // Fetch reviews after restoring session
        setTimeout(() => fetchReviews(session.pageAccessToken, session.page?.id), 100);
      } catch (err) {
        console.error('Failed to restore session:', err);
        localStorage.removeItem('repugate_session');
        sessionStorage.removeItem('repugate_session');
        setError('Session invalid. Please login again.');
      }
    }
  }, []);

  // Sort reviews by priority whenever they change
  useEffect(() => {
    if (reviews.length > 0) {
      const sorted = sortReviewsByPriority(reviews);
      // Automatically select first review
      if (!selectedReview) {
        setSelectedReview(sorted[0]);
      }
    }
  }, [reviews, selectedReview]);

  // Register keyboard shortcuts
  useKeyboardShortcuts({
    onNextReview: () => {
      const currentIdx = reviews.findIndex(r => r.id === selectedReview?.id);
      if (currentIdx < reviews.length - 1) {
        setSelectedReview(reviews[currentIdx + 1]);
        setGeneratedReplies(null);
        setSelectedOption(null);
      }
    },
    onPrevReview: () => {
      const currentIdx = reviews.findIndex(r => r.id === selectedReview?.id);
      if (currentIdx > 0) {
        setSelectedReview(reviews[currentIdx - 1]);
        setGeneratedReplies(null);
        setSelectedOption(null);
      }
    },
    onRegenerate: () => {
      if (generatedReplies) {
        // Find next tone
        const currentIdx = TONE_PRESETS.findIndex(t => t.key === selectedTone);
        const nextIdx = (currentIdx + 1) % TONE_PRESETS.length;
        setSelectedTone(TONE_PRESETS[nextIdx].key);
        handleGenerateReplies(selectedLanguage, TONE_PRESETS[nextIdx].key);
      }
    },
    onCopy: () => {
      if (selectedOption && generatedReplies) {
        handleCopyReply(getSelectedReplyText());
      }
    },
    onToggleHelp: () => {
      setShowKeyboardHelp(!showKeyboardHelp);
    }
  });

  const fetchReviews = async (token, pageId) => {
    try {
      setLoading(true);
      setError(null);

      // Use provided pageId or get from localStorage
      let pid = pageId;
      
      if (!pid) {
        const session = JSON.parse(localStorage.getItem('repugate_session') || sessionStorage.getItem('repugate_session') || '{}');
        pid = session.page?.id;
      }

      if (!pid || typeof pid !== 'string' || pid.trim() === '' || pid.startsWith('-')) {
        console.warn('Invalid pageId:', pid);
        setError('Invalid page ID. Please login again.');
        localStorage.removeItem('repugate_session');
        sessionStorage.removeItem('repugate_session');
        return;
      }

      console.log(`Fetching reviews for page: ${pid} with token: ${token?.substring(0, 20)}...`);

      const response = await axios.get(
        `${API_BASE_URL}/providers/facebook/reviews`,
        {
          params: {
            limit: 50,
            pageId: pid
          },
          headers: {
            'X-Facebook-Token': token
          }
        }
      );

      if (response.data && response.data.reviews) {
        setReviews(response.data.reviews);
        setStats(prev => ({
          ...prev,
          totalReviews: response.data.count || 0
        }));
      } else {
        setError('No reviews found. Click "Refresh Reviews" to load reviews from Facebook.');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message;
      setError(`Failed to fetch reviews: ${errorMsg}`);
      console.error('Fetch reviews error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReplies = async (language = 'english', tone = 'professional') => {
    if (!selectedReview) {
      setError('Please select a review first');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await axios.post(
        `${API_BASE_URL}/replies/generate`,
        {
          reviewId: selectedReview.id,
          pageId: page?.id || 'unknown',
          businessType: 'restaurant',
          language: language,
          tone: tone,
          reviewText: selectedReview.text
        }
      );

      setGeneratedReplies(response.data);
      setStats(prev => ({ ...prev, repliesGenerated: prev.repliesGenerated + 1 }));
      
      // Show any safety warnings
      if (response.data.safetyWarnings && response.data.safetyWarnings.length > 0) {
        console.warn('[SAFETY] Warnings in generated replies:', response.data.safetyWarnings);
      }
      
      setSuccessMessage('✨ Replies generated successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(`Failed to generate replies: ${err.message}`);
      console.error('Generate replies error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyReply = (replyText) => {
    try {
      navigator.clipboard.writeText(replyText);
      setSuccessMessage('✓ Reply copied to clipboard!');
      
      // Track first copy for next-step prompt
      if (!firstCopyDone) {
        setFirstCopyDone(true);
        // Show next-step message after 1 second
        setTimeout(() => {
          setSuccessMessage('✓ Copied! Tip: Press R to regenerate with a different tone, or N for the next review.');
        }, 1500);
      }
      
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (err) {
      setError('Failed to copy to clipboard');
      console.error('Copy error:', err);
    }
  };

  const getSelectedReplyText = () => {
    if (!selectedOption || !generatedReplies) return null;
    const optionKey = typeof selectedOption === 'number' ? `option${selectedOption}` : selectedOption;
    return generatedReplies.replies?.[optionKey];
  };

  const handleLogout = () => {
    sessionStorage.removeItem('repugate_session');
    localStorage.removeItem('repugate_session');
    setPage(null);
    setPageAccessToken(null);
    setReviews([]);
    setSelectedReview(null);
    setGeneratedReplies(null);
    setError(null);
    setSuccessMessage(null);
    onLogout();
  };

  const handleReviewsImported = (importedReviews) => {
    // Merge imported reviews with existing ones
    const mergedReviews = [...reviews, ...importedReviews];
    setReviews(mergedReviews);
    
    // Re-sort and auto-select first review
    const sorted = sortReviewsByPriority(mergedReviews);
    if (sorted.length > 0) {
      setSelectedReview(sorted[0]);
      setGeneratedReplies(null);
      setSelectedOption(null);
    }
    
    // Show success message
    setSuccessMessage(`✓ Imported ${importedReviews.length} review${importedReviews.length !== 1 ? 's' : ''}`);
    setShowImporter(false);
    
    // Clear success message after 3 seconds
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  // Main dashboard
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Guided Tour */}
      {showGuidedTour && (
        <GuidedTour
          onComplete={() => setShowGuidedTour(false)}
          onSkip={() => setShowGuidedTour(false)}
        />
      )}

      {/* Keyboard Shortcuts Help */}
      <KeyboardShortcutsHelp
        isOpen={showKeyboardHelp}
        onClose={() => setShowKeyboardHelp(false)}
      />

      {/* Review Importer Modal */}
      {showImporter && (
        <ReviewImporter
          onReviewsImported={handleReviewsImported}
          onClose={() => setShowImporter(false)}
        />
      )}

      {/* Header */}
      <header className="bg-black/40 backdrop-blur-md border-b border-purple-500/20 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src={logo} alt="RepuGate Logo" className="h-10 w-auto" />
              <div>
                <h1 className="text-4xl font-black bg-gradient-to-r from-purple-400 to-pink-400 text-transparent bg-clip-text">
                  RepuGate
                </h1>
                <p className="text-xs text-purple-300">Your Gateway to a Better Reputation</p>
                <p className="text-purple-300/80 text-sm">
                  <span className="font-semibold text-white">{page?.name || 'Connecting...'}</span>
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="hidden sm:block text-right text-purple-100">
                <div className="grid grid-cols-2 gap-4 text-xs">
                  <div className="flex flex-col items-center">
                    <p className="text-purple-400 font-semibold text-lg">{stats.totalReviews}</p>
                    <p className="text-purple-300/60">Reviews</p>
                  </div>
                  <div className="flex flex-col items-center">
                    <p className="text-purple-400 font-semibold text-lg">{stats.repliesGenerated}</p>
                    <p className="text-purple-300/60">Generated</p>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setShowKeyboardHelp(!showKeyboardHelp)}
                className="p-2 hover:bg-purple-600/30 rounded-lg transition text-purple-300 hover:text-purple-100 hidden sm:flex"
                title="Keyboard shortcuts (K)"
              >
                <HelpCircle size={20} />
              </button>
              <button
                onClick={handleLogout}
                className="px-4 py-2 bg-red-600/80 hover:bg-red-600 text-white rounded-lg transition-all font-semibold flex items-center gap-2 backdrop-blur-sm"
              >
                <LogOut size={16} />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Alerts */}
        {error && (
          <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 text-red-200 rounded-lg backdrop-blur-sm animate-in fade-in slide-in-from-top-2">
            <p className="font-semibold text-red-100">⚠️ Error</p>
            <p className="text-sm text-red-300/90">{error}</p>
          </div>
        )}

        {successMessage && (
          <div className="mb-6 p-4 bg-green-500/20 border border-green-500/50 text-green-200 rounded-lg backdrop-blur-sm animate-in fade-in slide-in-from-top-2">
            <p className="font-semibold text-green-100">✓ {successMessage}</p>
          </div>
        )}

        {/* Refresh Reviews Button */}
        <div className="mb-8 flex gap-2 flex-wrap">
          <button
            onClick={() => fetchReviews(pageAccessToken)}
            disabled={loading}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all ${
              loading
                ? 'bg-purple-600/50 text-purple-200 cursor-not-allowed'
                : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 active:scale-95 shadow-lg hover:shadow-xl'
              }`}
          >
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
            {loading ? 'Loading...' : 'Refresh Reviews'}
          </button>
          
          <button
            onClick={() => setShowImporter(true)}
            className="flex items-center gap-2 px-6 py-3 bg-slate-700/50 hover:bg-slate-700 text-purple-200 border border-purple-500/30 rounded-lg font-semibold transition-all"
            title="Import reviews from CSV or manual entry (fallback if Facebook has issues)"
          >
            <Upload size={18} />
            Import Reviews
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 animate-in fade-in duration-500">
          {/* Reviews List */}
          <div data-tour="review-list">
            <ReviewList
              reviews={reviews}
              selectedReview={selectedReview}
              onSelectReview={setSelectedReview}
              loading={loading}
            />
          </div>

          {/* Reply Generator */}
          <div className="bg-slate-800/50 backdrop-blur-sm border border-purple-500/20 rounded-2xl shadow-2xl p-8 lg:sticky lg:top-24">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
              <Sparkles className="text-pink-400" size={28} />
              AI Reply Generator
            </h2>

            {!selectedReview ? (
              <div className="text-center py-16 bg-slate-900/50 rounded-xl border-2 border-dashed border-purple-500/30">
                <MessageSquare className="w-12 h-12 text-purple-400/50 mx-auto mb-3" />
                <p className="text-purple-300/80 text-lg">
                  Select a review to generate AI replies
                </p>
              </div>
            ) : !generatedReplies ? (
              <div className="space-y-5">
                {/* Selected Review Preview */}
                <div className="bg-gradient-to-br from-purple-900/50 to-slate-900/50 p-5 rounded-xl border border-purple-500/30 backdrop-blur-sm">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-purple-100 text-sm">
                        Review from <span className="text-white">{selectedReview.reviewer_name || 'Anonymous'}</span>
                      </h3>
                    </div>
                    <span className="inline-flex items-center gap-1 bg-yellow-500/20 text-yellow-200 text-xs font-semibold px-3 py-1 rounded-full">
                      ⭐ {selectedReview.rating}/5
                    </span>
                  </div>
                  <p className="text-purple-100 text-sm leading-relaxed mb-3 italic">
                    "{selectedReview.text}"
                  </p>
                  <p className="text-xs text-purple-300/60">
                    {new Date(selectedReview.published_at).toLocaleDateString()}
                  </p>
                </div>

                {/* Language Selector */}
                <div>
                  <label className="block text-sm font-semibold text-purple-200 mb-3">
                    Select Reply Language
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { value: 'english', label: 'English' },
                      { value: 'bangla', label: 'Bangla' }
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => setSelectedLanguage(opt.value)}
                        className={`py-3 px-3 rounded-lg font-medium transition-all ${
                          selectedLanguage === opt.value
                            ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white border border-purple-400 shadow-lg'
                            : 'bg-slate-700/50 text-purple-100 border border-purple-500/30 hover:border-purple-500/60 hover:bg-slate-700'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Tone Selector */}
                <div data-tour="tone-selector">
                  <label className="block text-sm font-semibold text-purple-200 mb-3">
                    Choose Reply Tone
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {TONE_PRESETS.map((tone) => (
                      <button
                        key={tone.key}
                        onClick={() => setSelectedTone(tone.key)}
                        className={`py-3 px-3 rounded-lg transition-all text-left ${
                          selectedTone === tone.key
                            ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white border border-purple-400 shadow-lg'
                            : 'bg-slate-700/30 text-purple-100 border border-purple-500/20 hover:border-purple-500/50 hover:bg-slate-700/40'
                        }`}
                        title={tone.description}
                      >
                        <div className="text-sm font-medium">{tone.label}</div>
                        <div className="text-xs opacity-70 mt-0.5">{tone.description}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Generate Button */}
                <button
                  onClick={() => handleGenerateReplies(selectedLanguage, selectedTone)}
                  disabled={loading}
                  className={`w-full py-3 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 ${
                    loading
                      ? 'bg-purple-600/50 text-purple-200 cursor-not-allowed'
                      : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 active:scale-95 shadow-lg hover:shadow-xl'
                  }`}
                  data-tour="generate-button"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-transparent border-t-white"></div>
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles size={18} />
                      Generate 3 Reply Options
                    </>
                  )}
                </button>
              </div>
            ) : (
              <div className="space-y-5">
                {/* Generated Replies */}
                <div className="space-y-3">
                  {['option1', 'option2', 'option3'].map((option, index) => (
                    <div
                      key={option}
                      onClick={() => setSelectedOption(index + 1)}
                      className={`p-4 rounded-xl cursor-pointer transition-all border backdrop-blur-sm ${
                        selectedOption === (index + 1)
                          ? 'bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-green-500/50 shadow-lg shadow-green-500/20 ring-1 ring-green-500/30'
                          : 'bg-slate-700/30 border-purple-500/30 hover:border-purple-500/60 hover:bg-slate-700/40'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                          selectedOption === (index + 1)
                            ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white'
                            : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                        }`}>
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <p className="text-purple-100 text-sm leading-relaxed">
                            {generatedReplies.replies[option]}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Copy Reply Button */}
                <button
                  onClick={() => handleCopyReply(getSelectedReplyText())}
                  disabled={!selectedOption}
                  className={`w-full py-3 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 ${
                    selectedOption
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 active:scale-95 shadow-lg hover:shadow-xl'
                      : 'bg-slate-700/50 text-purple-300/50 cursor-not-allowed'
                  }`}
                  data-tour="reply-options"
                >
                  <Copy size={18} />
                  Copy Reply to Clipboard
                </button>

                {/* Regenerate with Different Tone Button */}
                <button
                  onClick={() => {
                    const currentIdx = TONE_PRESETS.findIndex(t => t.key === selectedTone);
                    const nextIdx = (currentIdx + 1) % TONE_PRESETS.length;
                    setSelectedTone(TONE_PRESETS[nextIdx].key);
                    handleGenerateReplies(selectedLanguage, TONE_PRESETS[nextIdx].key);
                  }}
                  disabled={loading}
                  className={`w-full py-2.5 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 ${
                    loading
                      ? 'bg-slate-700/50 text-purple-300/50 cursor-not-allowed'
                      : 'bg-slate-700/50 text-purple-200 border border-purple-500/30 hover:bg-slate-700/80 hover:border-purple-500/60'
                  }`}
                >
                  <RotateCcw size={16} />
                  Try Different Tone (R)
                </button>

                {/* Info Message */}
                <div className="text-sm text-center text-purple-300 mt-3">
                  <p>📋 Copy the reply above and paste it manually on Facebook</p>
                </div>

                <button
                  onClick={() => {
                    setGeneratedReplies(null);
                    setSelectedOption(null);
                  }}
                  disabled={loading}
                  className="w-full py-2.5 text-purple-200 border border-purple-500/30 rounded-lg hover:bg-slate-700/50 hover:border-purple-500/60 transition-all flex items-center justify-center gap-2"
                >
                  <ChevronLeft size={16} />
                  Generate New Replies
                </button>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Compliance & Privacy Footer */}
      <ComplianceFooter onDisconnect={handleLogout} />
    </div>
  );
}

export default Dashboard;
